import { omit } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import type { IValidationSchema, ISchemaBoundValidationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { newCid } from '@/features/legends/editor/collections'
import type { AnswerOptions } from '@/features/legends/editor/entities'
import { createEntityIdValidation } from '@/features/legends/editor/entity-ids'
import type { RynoType } from '@/features/legends/editor/ryno-types'
import { validateWhileEditing } from '@/features/legends/editor/validation'
import { ViewRequest } from '@/features/legends/editor/view-request'
import { EffectEditorViewModel } from '@/features/legends/effects/EffectEditorViewModel'
import { FiltersBuilderViewModel } from '@/features/legends/filters/FiltersBuilderViewModel'
import type { FilterScope } from '@/features/legends/filters/filter-scope'
import { validateObject } from '@/utils/validx-util'
import type { QuestionsBuilderViewModel } from './QuestionsBuilderViewModel'
import type { Question, QuestionBase } from './questions'
import type { DropPosition } from './question-drag'
import {
  type QuestionType,
  type QuestionTypeInfo,
  questionTypeInfo,
} from './question-types'

export interface QuestionEditorViewModelOptions {
  dependencies: LegendPartDependencies
  parentQuestionsBuilder: QuestionsBuilderViewModel
  scope: readonly FilterScope[]
}

/**
 * One of a question's effects, as listed in the inspector's Effects tab.
 */
export interface EffectDescriptor {
  id: string
  title: string
  effect: EffectEditorViewModel
}

/**
 * Extra conditions of a question, like one per select option.
 */
export interface TriggerWhenDescriptor {
  id: string
  title: string
  triggerWhen: FiltersBuilderViewModel
}

/**
 * Base for all question editors. Questions are the elements of a legend's
 * intake: asked questions, and meta elements like paths and variables. Each
 * type validates itself with its own schema, which starts with
 * {@link QuestionEditorViewModelBase.sharedSchema}, or only
 * {@link QuestionEditorViewModelBase.triggerWhenValidators} for types without an
 * ID.
 */
export abstract class QuestionEditorViewModelBase<
  TQuestion extends Question = Question,
> extends LegendPartViewModelBase {
  protected static readonly idValidation =
    createEntityIdValidation<QuestionEditorViewModelBase>(
      (question) => question.dependencies.symbols.allEntities,
    )

  protected static readonly triggerWhenValidators = [
    validateObject<QuestionEditorViewModelBase>(
      'There are invalid conditions.',
    ),
  ]

  /**
   * The ID and conditions, which every type with an ID validates.
   */
  protected static readonly sharedSchema: IValidationSchema<QuestionEditorViewModelBase> =
    {
      id: QuestionEditorViewModelBase.idValidation.validators,
      triggerWhen: QuestionEditorViewModelBase.triggerWhenValidators,
    }

  private readonly _idFieldFocusRequest = new ViewRequest()
  private readonly _scrollIntoViewRequest = new ViewRequest()

  protected readonly scope: readonly FilterScope[]

  /**
   * Identifies the question on the client, for keys.
   */
  readonly cid = newCid('question')

  /**
   * The question type, as stored in the legend.
   */
  abstract readonly type: QuestionType

  /**
   * The question's validation, bound to its type's schema.
   */
  abstract readonly validation: ISchemaBoundValidationContext<QuestionEditorViewModelBase>

  /**
   * When the question is asked.
   */
  readonly triggerWhen: FiltersBuilderViewModel

  /**
   * The ID it had when loaded or duplicated. Duplicates derive their IDs from
   * it so copies of copies count up instead of nesting suffixes.
   */
  lastKnownId: string | null = null

  /**
   * Whether a path's questions are hidden on the canvas.
   */
  @observable accessor isCollapsed = false

  /**
   * The builder that holds the question.
   */
  @observable.ref accessor parentQuestionsBuilder: QuestionsBuilderViewModel

  /**
   * The ID other elements reference the question by.
   */
  @observable accessor id = ''

  /**
   * The ID as typed. It becomes the ID once committed, if it is valid.
   */
  @observable accessor idDraft = ''

  /**
   * Asks the inspector to focus the ID field.
   */
  readonly idFieldFocusRequest$ = this._idFieldFocusRequest.request$

  /**
   * Asks the canvas to scroll the question into view.
   */
  readonly scrollIntoViewRequest$ = this._scrollIntoViewRequest.request$

  /**
   * Whether what is being dragged can be dropped next to the question. A
   * field, since the canvas calls it without the question.
   */
  readonly canAcceptDrop = () => this.parentQuestionsBuilder.canDropNextTo(this)

  constructor({
    dependencies,
    parentQuestionsBuilder,
    scope,
  }: QuestionEditorViewModelOptions) {
    super(dependencies)
    this.parentQuestionsBuilder = parentQuestionsBuilder
    this.scope = scope
    this.triggerWhen = this.createFiltersBuilder()
    this.lifecycle.manage(this.triggerWhen)
  }

  /**
   * How the question's type shows in the builder.
   */
  get info(): QuestionTypeInfo {
    return questionTypeInfo[this.type]
  }

  /* eslint-disable @typescript-eslint/class-literal-property-style -- subclasses override these with getters, which TypeScript doesn't allow over fields */
  /**
   * How to pick one of the answers, for filters and template mappings.
   * `false` when the question has no answers to pick from.
   */
  get answerOptions(): AnswerOptions | null | false {
    return false
  }

  /**
   * The type of the question's value, which makes it a symbol other elements
   * can reference. `null` for elements without a value.
   */
  get valueType(): RynoType | null {
    return null
  }

  /**
   * The question's effects, for the inspector's Effects tab.
   */
  get effectDescriptors(): EffectDescriptor[] | null {
    return null
  }

  /**
   * Conditions besides the question's own, for the inspector's Conditions
   * tab.
   */
  get triggerWhenDescriptors(): TriggerWhenDescriptor[] | null {
    return null
  }

  /**
   * The question's human-readable text: its label or its question.
   */
  get displayText(): string {
    return ''
  }

  /**
   * The title on the question's card. Meta elements show their text; asked
   * questions show their question in the card's preview instead.
   */
  get cardTitle(): string {
    return this.info.isMetaElement ? this.displayText : ''
  }
  /* eslint-enable @typescript-eslint/class-literal-property-style */

  /**
   * The paths the question is nested in, outermost first.
   */
  @computed
  get ancestors(): QuestionEditorViewModelBase[] {
    const ancestors: QuestionEditorViewModelBase[] = []
    let parent = this.parentQuestionsBuilder.parentQuestion
    while (parent) {
      ancestors.unshift(parent)
      parent = parent.parentQuestionsBuilder.parentQuestion
    }
    return ancestors
  }

  /**
   * The paths the question is nested in, by ID or text, as a breadcrumb.
   */
  @computed
  get ancestorPath() {
    return this.ancestors
      .map((ancestor) => ancestor.id || ancestor.displayText || 'Untitled')
      .join(' / ')
  }

  /**
   * The number of conditions on the question itself.
   */
  @computed
  get conditionCount() {
    return this.triggerWhen.length
  }

  /**
   * Whether the inspector has the question selected.
   */
  @computed
  get isSelected(): boolean {
    return this.parentQuestionsBuilder.selectedQuestion?.cid === this.cid
  }

  @computed
  private get _json(): TQuestion {
    return this._serialize()
  }

  /**
   * Whether this question is `question` or nested in it.
   */
  isChildOf(question: QuestionEditorViewModelBase): boolean {
    return this === question || this.ancestors.includes(question)
  }

  /**
   * Elements nested in this one. Only paths have any.
   */
  getAllSubQuestions(): QuestionEditorViewModelBase[] {
    return []
  }

  /**
   * Creates a builder for conditions that see the same symbols as the
   * question. Its owner manages it.
   */
  createFiltersBuilder() {
    return new FiltersBuilderViewModel({
      dependencies: this.dependencies,
      scope: this.scope,
      context: this,
    })
  }

  /**
   * Creates an effect that belongs to the question. Its owner manages it.
   */
  createEffect() {
    return new EffectEditorViewModel({
      dependencies: this.dependencies,
      context: this,
    })
  }

  /**
   * Sets the ID to the draft, when it is valid and unique.
   */
  @action.bound
  commitId() {
    QuestionEditorViewModelBase.idValidation.trySetId(this, this.idDraft)
  }

  /**
   * Sets the ID without validation, for generated IDs.
   */
  @action
  assignId(id: string) {
    this.id = id
    this.idDraft = id
  }

  /**
   * Moves the question to another builder. The builders' lists are updated
   * by the caller.
   */
  @action
  setParentQuestionsBuilder(builder: QuestionsBuilderViewModel) {
    this.parentQuestionsBuilder = builder
  }

  /**
   * Selects the question in the inspector.
   */
  @action.bound
  select() {
    this.parentQuestionsBuilder.edit(this)
  }

  /**
   * Removes the question from its builder.
   */
  @action.bound
  remove() {
    this.parentQuestionsBuilder.remove(this)
  }

  /**
   * Inserts a copy of the question after it.
   */
  @action.bound
  duplicate() {
    this.parentQuestionsBuilder.duplicate(this)
  }

  /**
   * Focuses the inspector's ID field.
   */
  @action.bound
  focusIdField() {
    this._idFieldFocusRequest.request()
  }

  /**
   * Scrolls the canvas to the question.
   */
  scrollIntoView() {
    this._scrollIntoViewRequest.request()
  }

  /**
   * Shows or hides a path's questions. Toggles when `isCollapsed` is left
   * out; other types can't collapse.
   */
  @action.bound
  toggleCollapsed(isCollapsed?: boolean) {
    if (!this.info.collapsible) {
      return
    }
    this.isCollapsed = isCollapsed ?? !this.isCollapsed
  }

  /**
   * Starts dragging the question to another place on the canvas.
   */
  @action.bound
  startDrag() {
    this.parentQuestionsBuilder.startDrag({ kind: 'question', question: this })
  }

  /**
   * Drops what is being dragged next to the question.
   */
  @action.bound
  acceptDrop(position: DropPosition) {
    this.parentQuestionsBuilder.dropNextTo(this, position)
  }

  /**
   * Moves the question one place up or down among its siblings, as the
   * keyboard does. It stays in its path, like a drop next to a sibling.
   */
  @action.bound
  moveBy(delta: -1 | 1) {
    const builder = this.parentQuestionsBuilder
    const sibling = builder.questions[builder.questions.indexOf(this) + delta]
    if (this.isEditingDisabled || !sibling) {
      return
    }
    builder.move(this, sibling, delta > 0 ? 1 : 0)
  }

  /**
   * Shows the analyzer's errors for the question.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    addGenericErrorForProperty(
      this,
      descriptor.inner?.triggerWhen,
      'triggerWhen',
      'There are invalid conditions.',
    )
    receiveErrors(this, descriptor.inner)
  }

  /**
   * The question as the legend document stores it. Computed, so an
   * unchanged question keeps its reference and doesn't end up in the next
   * save's diff.
   */
  toJSON(): TQuestion {
    return this._json
  }

  /**
   * Sets the question from the legend document.
   */
  abstract hydrate(question: TQuestion): void

  /**
   * The element of the question's type.
   */
  protected abstract _serialize(): TQuestion

  /**
   * The ID and conditions, as the element stores them.
   */
  protected _serializeShared(): Required<QuestionBase> {
    return { id: this.id, triggerWhen: this.triggerWhen.toJSON() }
  }

  /**
   * Loads the ID and conditions. Each type's `hydrate` loads its own fields
   * and calls this.
   */
  @action
  protected _hydrateShared(question: QuestionBase) {
    this.id = question.id ?? ''
    this.idDraft = this.id
    if (question.id) {
      this.lastKnownId = question.id
    }
    this.triggerWhen.hydrate(question.triggerWhen)
  }

  /**
   * Validates the type's fields as they change. The ID is left out; it is
   * validated when it is committed.
   *
   * @param schema The schema the question's validation context is bound to.
   * @param cascades Fields that also validate other fields when they change.
   */
  protected _validateWhileEditing<T extends QuestionEditorViewModelBase>(
    this: T,
    schema: IValidationSchema<T>,
    cascades: Partial<Record<keyof T & string, (keyof T & string)[]>> = {},
  ) {
    this.lifecycle.manage(
      validateWhileEditing(
        this,
        // SAFETY: every field of a schema is optional, so one without a field
        // is still a schema.
        omit(schema, 'id') as IValidationSchema<T>,
        cascades,
      ),
    )
  }
}
